import React from 'react'

import { Alert } from 'react-bootstrap'
import {
  Checkbox, MultiLineField, Label, validateFieldsEx,
  BooleanRadioButtonField,
  yesNoDataSource,
} from '../../../../shared/fields'
import { ApplicationModel } from '../../../../models/ApplicationModel'
import EMDGAccordion from '../../../../shared/EMDGAccordion'
import { getLastFY } from '../../../../shared/Utility'
import { GRANT_GUIDELINES_URL } from '../..'

export interface Props {
    model: ApplicationModel
    onChange: { (changes: Partial<ApplicationModel>, callback?: { (): any }): any }
    preview?: boolean
}

export interface State {
  validationError: boolean,
}

export default class RepresentativeAgreement extends React.Component<Props, State> {
  hiddenCheckboxForValidation: Checkbox
  expenseAreExpandingExportPromotion: Checkbox
  expenseEligibleExpensesTraining: Checkbox
  expenseEligibleExpensesNotMet: Checkbox
  doPromoteAustralianBusinessInterests: BooleanRadioButtonField
  describePromoteAustralianBusinessInterests: MultiLineField
  doRepresentSmeMembersInIndustry: BooleanRadioButtonField
  describeRepresentSmeMembersInIndustry: MultiLineField
  areSmeMembersRepresentProportionOfBusiness: BooleanRadioButtonField
  describeSmeMembersRepresentProportionOfBusiness: MultiLineField
  doDistributeIncomeToSmeMembers: BooleanRadioButtonField
  describeNotForProfitBusinessStructure: MultiLineField
  areSmeMembersExportingOrReadyToExport: BooleanRadioButtonField
  describeSmeMembersExportingOrReadyToExport: MultiLineField
  hasDesignatedConnectionToEligibleProducts: BooleanRadioButtonField
  designatedConnectionToEligibleProducts: MultiLineField
  doExportEligibleProductsAsPrimaryActivity: BooleanRadioButtonField
  describeExportEligibleProductsAsPrimaryActivity: MultiLineField

  constructor(props: Props) {
    super(props)

    this.state = {
      validationError: false,
    }
  }

  componentDidUpdate() {
    const { model } = this.props
    const { validationError } = this.state

    if (validationError && (
      model.expenseAreExpandingExportPromotion
      || model.expenseEligibleExpensesTraining
      || model.expenseEligibleExpensesNotMet)
    ) this.setState({ validationError: false })
  }

  handleExpenseEligibleExpensesNotMet = (val: boolean) => {
    const { onChange } = this.props

    if (val) {
      onChange({ expenseEligibleExpensesNotMet: val || null, expenseEligibleExpensesTraining: null, expenseAreExpandingExportPromotion: null })
    } else {
      onChange({ expenseEligibleExpensesNotMet: val || null })
    }
  }

  validate(callback: { (res: boolean): any }, focus?: boolean) {
    const { model } = this.props
    const isActivitySelected = model.expenseAreExpandingExportPromotion || model.expenseEligibleExpensesTraining

    if (model.expenseEligibleExpensesNotMet
      || (model.expenseAreExpandingExportPromotion && model.doPromoteAustralianBusinessInterests === false)
      || (isActivitySelected
          && (
            model.doRepresentSmeMembersInIndustry === false
            || model.areSmeMembersRepresentProportionOfBusiness === false
            || model.doDistributeIncomeToSmeMembers === true
            || model.areSmeMembersExportingOrReadyToExport === false
            || model.hasDesignatedConnectionToEligibleProducts === false
          ))
    ) {
      callback && callback(false) // don't ever continue!
      return
    }
    this.setState({
      validationError: !model.expenseAreExpandingExportPromotion
              && !model.expenseEligibleExpensesTraining
              && !model.expenseEligibleExpensesNotMet,
    }, () => {
      validateFieldsEx(
        focus,
        callback,
        this.hiddenCheckboxForValidation,
        this.doPromoteAustralianBusinessInterests,
        this.describePromoteAustralianBusinessInterests,
        this.doRepresentSmeMembersInIndustry,
        this.describeRepresentSmeMembersInIndustry,
        this.areSmeMembersRepresentProportionOfBusiness,
        this.describeSmeMembersRepresentProportionOfBusiness,
        this.doDistributeIncomeToSmeMembers,
        this.describeNotForProfitBusinessStructure,
        this.areSmeMembersExportingOrReadyToExport,
        this.describeSmeMembersExportingOrReadyToExport,
        this.hasDesignatedConnectionToEligibleProducts,
        this.designatedConnectionToEligibleProducts,
        this.doExportEligibleProductsAsPrimaryActivity,
        this.describeExportEligibleProductsAsPrimaryActivity,
      )
    })
  }

  renderAlertNotEligibleForGrant() {
    return (
      <Alert className="emdg-page-alerts emdg-page-alerts--error">
        You are
        {' '}
        <b>not</b>
        {' '}
        eligible to apply for a representative body grant.
      </Alert>
    )
  }

  renderAlertNotEligibleAsRepBody() {
    return (
      <Alert className="emdg-page-alerts emdg-page-alerts--error">
        You are
        {' '}
        <b>not</b>
        {' '}
        eligible to apply as a Representative body.
      </Alert>
    )
  }

  render() {
    const { model, onChange, preview } = this.props
    const { validationError } = this.state
    const isActivitySelected = model.expenseAreExpandingExportPromotion || model.expenseEligibleExpensesTraining

    const labelSelectActivity = '39. Select one or both activities for which you are applying for as a Representative body. *'
    const labelExpenseAreExpandingExportPromotion = 'Undertaking new export marketing and promotional activities on behalf of your members'
    const labelExpenseEligibleExpensesTraining = 'Providing export training to your members to help them become export ready'
    const labelNoneOfTheAbove = 'None of the above'

    const labelDoPromoteAustralianBusinessInterests = '40. Do you promote the interests of a group of Australian businesses (SME members)?'
    const labelDescribePromoteAustralianBusinessInterests = '40.a Provide details about your membership and how you promote their interests.'
    const labelDoRepresentSmeMembersInIndustry = '41. Do you represent SME members from an industry, a substantial part of an industry or more than one industry?'
    const labelDescribeRepresentSmeMembersInIndustry = '41.a Provide details about the industry/industries you represent. '
      + 'This may include regional-specific representation of a certain industry or broad representation across multiple industries.'
    const labelAreSmeMembersRepresentProportionOfBusiness = '42. Do the number of the SME members in your group represent a substantial '
      + 'proportion of businesses of the industry or industries you represent?'
    const labelDescribeSmeMembersRepresentProportionOfBusiness = '42.a Provide what proportion of businesses of the industry or industries your members represent. '
      + 'You must provide detail about both the size and the proportion of your representation comparatively for Austrade to determine if your representation is substantial.'
    const labelDoDistributeIncomeToSmeMembers = '43. Do you distribute income to your SME members or shareholders?'
    const labelDescribeNotForProfitBusinessStructure = '43.a Confirm you are not-for-profit and how your business structure reflects this.'
    const labelAreSmeMembersExportingOrReadyToExport = '44. Are all or a substantial number of the SME members of the represented group exporting or ready to export eligible products?'
    const labelDescribeSmeMembersExportActivities = '44.a Describe the export activities that your SME members are undertaking or seeking to '
    + 'undertake, including the percentage of those members exporting or ready to export eligible products.'
    const labelHasDesignatedConnectionToEligibleProducts = '45. Do your SME members have a designated connection to eligible products?'
    const labelDesignatedConnectionToEligibleProducts = '45.a Explain how your SME members have a designated connection to their eligible products.'
    const labelDoExportEligibleProductsAsPrimaryActivity = '46. Do you consider the export of eligible products as one of your primary activities?'
    const labelDescribeExportOfEligibleProductsAsPrimaryActivity = '46.a Provide further information. '
    + 'You will not be eligible to apply as a Representative body if this is one of your primary activities.'

    const lastFYStr = getLastFY(new Date())

    return (
      <div className="form-item">
        <div>
          In line with the objectives of the EMDG Act, proposed activities to be undertaken by the Representative body must have a direct link to export and must support Australian SMEs.
          {' '}
          For the purposes of the EMDG program, SMEs are defined as being businesses with an annual turnover of less than $20 million.
        </div>
        { preview && <h4>Representative body agreement</h4>}
        <EMDGAccordion expanded header="Representative body agreement">
          <>
            <Label
              label={labelSelectActivity}
              preview={preview}
            />

            <Checkbox
              ref={(r) => { this.hiddenCheckboxForValidation = r }}
              id="hiddenCheckboxForValidation"
              required={!model.expenseAreExpandingExportPromotion
                  && !model.expenseEligibleExpensesTraining
                  && !model.expenseEligibleExpensesNotMet}
              onChange={() => {}}
              value={false}
              label=""
              className="hidden-checkbox"
            />
            <Checkbox
              ref={(r) => { this.expenseAreExpandingExportPromotion = r }}
              id="expenseAreExpandingExportPromotion"
              required={false}
              onChange={(val) => onChange({ expenseAreExpandingExportPromotion: val || null })}
              value={model.expenseAreExpandingExportPromotion}
              label={labelExpenseAreExpandingExportPromotion}
              disabled={model.expenseEligibleExpensesNotMet}
              preview={preview}
              validateError={validationError}
            />

            <Checkbox
              ref={(r) => { this.expenseEligibleExpensesTraining = r }}
              id="expenseEligibleExpensesTraining"
              required={false}
              onChange={(val) => onChange({ expenseEligibleExpensesTraining: val || null })}
              value={model.expenseEligibleExpensesTraining}
              label={labelExpenseEligibleExpensesTraining}
              disabled={model.expenseEligibleExpensesNotMet}
              preview={preview}
              validateError={validationError}
            />

            <Checkbox
              ref={(r) => { this.expenseEligibleExpensesNotMet = r }}
              id="expenseEligibleExpensesNotMet"
              required={false}
              onChange={(val) => this.handleExpenseEligibleExpensesNotMet(val)}
              value={model.expenseEligibleExpensesNotMet}
              label={labelNoneOfTheAbove}
              preview={preview}
              validateError={validationError}
            />
            <div>
              <div className="mt-3">
                <b>Note: </b>
                You must select one or both activities. If you select ‘none of the above’ you are not eligible to apply for a representative body grant.
              </div>
              <div className="mt-2">You must provide a copy of your Memorandum of Incorporation, Articles of Association or Constitution to demonstrate that you are a Representative body.</div>
            </div>
            <Alert className="emdg-page-alerts">
              <b>New Representative body requirements:</b>
              <ul>
                <li className="mt-0">
                  If you select ‘new promotional activities’ – you must complete the plan to market section of this application form.
                  {' '}
                  Your responses must be unique, high-quality and tailored to target your SME members.
                </li>
                <li>
                  If you select ‘training activities’ – you must upload an export training plan (template available at www.austrade.gov.au/EMDG)
                  {' '}
                  that is unique, high-quality and tailored to target your SME members.
                </li>
                <li>
                  If you select both activities, you will need to complete both the plan to market and export training plan.
                </li>
              </ul>
              <div className="mt-3">
                Sections 4.1.2.2 to 4.1.2.3 of the
                {' '}
                <a target="blank" href={`${GRANT_GUIDELINES_URL}`} rel="noreferrer">Grant Guidelines</a>
                {' '}
                provides further information on new export marketing and promotional activities,
                {' '}
                plan to market and export training plan requirements for Representative bodies.
              </div>
            </Alert>
            {validationError ? (<div className="form-error">This field is required</div>) : null}
            {model.expenseAreExpandingExportPromotion && (
              <>
                <BooleanRadioButtonField
                  ref={(r) => { this.doPromoteAustralianBusinessInterests = r }}
                  id="doPromoteAustralianBusinessInterests"
                  label={labelDoPromoteAustralianBusinessInterests}
                  value={model.doPromoteAustralianBusinessInterests}
                  onChange={(value) => onChange({ doPromoteAustralianBusinessInterests: value })}
                  required={model.expenseAreExpandingExportPromotion}
                  dataSource={yesNoDataSource}
                  disabled={!model.expenseAreExpandingExportPromotion}
                  preview={preview}
                />
                {model.doPromoteAustralianBusinessInterests && (
                <MultiLineField
                  ref={(r) => { this.describePromoteAustralianBusinessInterests = r }}
                  id="describePromoteAustralianBusinessInterests"
                  label={labelDescribePromoteAustralianBusinessInterests}
                  value={model.describePromoteAustralianBusinessInterests}
                  onChange={(value) => onChange({ describePromoteAustralianBusinessInterests: value })}
                  required={model.doPromoteAustralianBusinessInterests}
                  maxLength={3000}
                  preview={preview}
                />
                )}
                {model.doPromoteAustralianBusinessInterests === false && this.renderAlertNotEligibleAsRepBody()}
              </>
            )}
            {isActivitySelected && (
              <>
                <BooleanRadioButtonField
                  ref={(r) => { this.doRepresentSmeMembersInIndustry = r }}
                  id="doRepresentSmeMembersInIndustry"
                  label={labelDoRepresentSmeMembersInIndustry}
                  value={model.doRepresentSmeMembersInIndustry}
                  onChange={(value) => onChange({ doRepresentSmeMembersInIndustry: value })}
                  required={isActivitySelected}
                  dataSource={yesNoDataSource}
                  disabled={!isActivitySelected}
                  preview={preview}
                />
                {model.doRepresentSmeMembersInIndustry && (
                <MultiLineField
                  ref={(r) => { this.describeRepresentSmeMembersInIndustry = r }}
                  id="describeRepresentSmeMembersInIndustry"
                  label={labelDescribeRepresentSmeMembersInIndustry}
                  value={model.describeRepresentSmeMembersInIndustry}
                  onChange={(value) => onChange({ describeRepresentSmeMembersInIndustry: value })}
                  required={model.doRepresentSmeMembersInIndustry}
                  maxLength={3000}
                  preview={preview}
                />
                )}
                {model.doRepresentSmeMembersInIndustry === false && this.renderAlertNotEligibleAsRepBody()}

                <BooleanRadioButtonField
                  ref={(r) => { this.areSmeMembersRepresentProportionOfBusiness = r }}
                  id="areSmeMembersRepresentProportionOfBusiness"
                  label={labelAreSmeMembersRepresentProportionOfBusiness}
                  value={model.areSmeMembersRepresentProportionOfBusiness}
                  onChange={(value) => onChange({ areSmeMembersRepresentProportionOfBusiness: value })}
                  required={isActivitySelected}
                  dataSource={yesNoDataSource}
                  disabled={!isActivitySelected}
                  preview={preview}
                />
                {model.areSmeMembersRepresentProportionOfBusiness && (
                <MultiLineField
                  ref={(r) => { this.describeSmeMembersRepresentProportionOfBusiness = r }}
                  id="describeSmeMembersRepresentProportionOfBusiness"
                  label={labelDescribeSmeMembersRepresentProportionOfBusiness}
                  value={model.describeSmeMembersRepresentProportionOfBusiness}
                  onChange={(value) => onChange({ describeSmeMembersRepresentProportionOfBusiness: value })}
                  required={model.areSmeMembersRepresentProportionOfBusiness}
                  maxLength={3000}
                  preview={preview}
                />
                )}
                {model.areSmeMembersRepresentProportionOfBusiness === false && this.renderAlertNotEligibleAsRepBody()}

                <BooleanRadioButtonField
                  ref={(r) => { this.doDistributeIncomeToSmeMembers = r }}
                  id="doDistributeIncomeToSmeMembers"
                  label={labelDoDistributeIncomeToSmeMembers}
                  value={model.doDistributeIncomeToSmeMembers}
                  onChange={(value) => onChange({ doDistributeIncomeToSmeMembers: value })}
                  required={isActivitySelected}
                  dataSource={yesNoDataSource}
                  disabled={!isActivitySelected}
                  preview={preview}
                />
                {model.doDistributeIncomeToSmeMembers === false && (
                  <>
                    <Alert className="emdg-page-alerts">
                      You will be asked to provide a copy of your profit and loss statement and balance sheet for the last Australian financial year.
                    </Alert>
                    <MultiLineField
                      ref={(r) => { this.describeNotForProfitBusinessStructure = r }}
                      id="describeNotForProfitBusinessStructure"
                      label={labelDescribeNotForProfitBusinessStructure}
                      value={model.describeNotForProfitBusinessStructure}
                      onChange={(value) => onChange({ describeNotForProfitBusinessStructure: value })}
                      required={!model.doDistributeIncomeToSmeMembers}
                      maxLength={3000}
                      preview={preview}
                    />
                  </>
                )}
                {model.doDistributeIncomeToSmeMembers && this.renderAlertNotEligibleAsRepBody()}

                <BooleanRadioButtonField
                  ref={(r) => { this.areSmeMembersExportingOrReadyToExport = r }}
                  id="areSmeMembersExportingOrReadyToExport"
                  label={labelAreSmeMembersExportingOrReadyToExport}
                  value={model.areSmeMembersExportingOrReadyToExport}
                  onChange={(value) => onChange({ areSmeMembersExportingOrReadyToExport: value })}
                  required={isActivitySelected}
                  dataSource={yesNoDataSource}
                  disabled={!isActivitySelected}
                  preview={preview}
                />
                {model.areSmeMembersExportingOrReadyToExport && (
                <MultiLineField
                  ref={(r) => { this.describeSmeMembersExportingOrReadyToExport = r }}
                  id="describeSmeMembersExportActivities"
                  label={labelDescribeSmeMembersExportActivities}
                  value={model.describeSmeMembersExportingOrReadyToExport}
                  onChange={(value) => onChange({ describeSmeMembersExportingOrReadyToExport: value })}
                  required={model.areSmeMembersExportingOrReadyToExport}
                  maxLength={3000}
                  preview={preview}
                />
                )}
                {model.areSmeMembersExportingOrReadyToExport === false && this.renderAlertNotEligibleAsRepBody()}

                <BooleanRadioButtonField
                  ref={(r) => { this.hasDesignatedConnectionToEligibleProducts = r }}
                  id="hasDesignatedConnectionToEligibleProducts"
                  label={labelHasDesignatedConnectionToEligibleProducts}
                  value={model.hasDesignatedConnectionToEligibleProducts}
                  onChange={(value) => onChange({ hasDesignatedConnectionToEligibleProducts: value })}
                  required={isActivitySelected}
                  dataSource={yesNoDataSource}
                  disabled={!isActivitySelected}
                  preview={preview}
                  description={(
                    <i>
                      This is demonstrated through their ownership of the eligible product (goods, services, events, software)
                      {' '}
                      through evidence such as licenses, intellectual property, written agreements, copyright or know-how. Refer to section 5.3.3.6 of the
                      {' '}
                      <a target="blank" href={`${GRANT_GUIDELINES_URL}`} rel="noreferrer">Grant Guidelines</a>
                      {' '}
                      for further information.
                    </i>
              )}
                />
                {model.hasDesignatedConnectionToEligibleProducts && (
                <MultiLineField
                  ref={(r) => { this.designatedConnectionToEligibleProducts = r }}
                  id="designatedConnectionToEligibleProducts"
                  label={labelDesignatedConnectionToEligibleProducts}
                  value={model.designatedConnectionToEligibleProducts}
                  onChange={(value) => onChange({ designatedConnectionToEligibleProducts: value })}
                  required={model.hasDesignatedConnectionToEligibleProducts}
                  maxLength={3000}
                  preview={preview}
                />
                )}
                {model.hasDesignatedConnectionToEligibleProducts === false && this.renderAlertNotEligibleAsRepBody()}

                <BooleanRadioButtonField
                  ref={(r) => { this.doExportEligibleProductsAsPrimaryActivity = r }}
                  id="doExportEligibleProductsAsPrimaryActivity"
                  label={labelDoExportEligibleProductsAsPrimaryActivity}
                  value={model.doExportEligibleProductsAsPrimaryActivity}
                  onChange={(value) => onChange({ doExportEligibleProductsAsPrimaryActivity: value })}
                  required={isActivitySelected}
                  dataSource={yesNoDataSource}
                  disabled={!isActivitySelected}
                  preview={preview}
                />
                {model.doExportEligibleProductsAsPrimaryActivity && (
                <MultiLineField
                  ref={(r) => { this.describeExportEligibleProductsAsPrimaryActivity = r }}
                  id="describeExportOfEligibleProductsAsPrimaryActivity"
                  label={labelDescribeExportOfEligibleProductsAsPrimaryActivity}
                  value={model.describeExportEligibleProductsAsPrimaryActivity}
                  onChange={(value) => onChange({ describeExportEligibleProductsAsPrimaryActivity: value })}
                  required={model.doExportEligibleProductsAsPrimaryActivity}
                  maxLength={3000}
                  preview={preview}
                />
                )}
              </>
            )}

            {model.expenseEligibleExpensesNotMet && this.renderAlertNotEligibleForGrant()}
          </>
        </EMDGAccordion>
      </div>
    )
  }
}
